import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonModule } from '@angular/common';
import { StudentAttachmentService } from '../student-attachment.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { CommonService } from '../../../../../shared/services/common.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AttachmentMode } from '../../../insights.enums';
import {
  Folder,
  ManageDrawerParams,
  MediaType,
} from '../../../insights.interface';
import find from 'lodash/find';
import { AttachmentFolderService } from '../attachment-folder.service';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../../audit-logs/audit-logs.interface';

@Component({
  selector: 'app-add-attachment',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputModule,
    NzSelectModule,
    NzUploadModule,
    NzButtonModule,
    CommonModule,
    ReactiveFormsModule,
    SvgIconComponent,
    NzSpinModule,
  ],
  templateUrl: './add-attachment.component.html',
})
export class AddAttachmentComponent implements OnDestroy {
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024;
  readonly MIN_NAME_LENGTH = 3;
  readonly MAX_NAME_LENGTH = 100;
  readonly MIN_DESCRIPTION_LENGTH = 10;
  readonly MAX_DESCRIPTION_LENGTH = 500;
  mode: AttachmentMode = AttachmentMode.CREATE;
  attachmentForm!: FormGroup;

  private caseParams!: ManageDrawerParams;

  private readonly destroy$ = new Subject<void>();

  fileList: NzUploadFile[] = [];
  folders: Folder[] = [];
  planTypeId: number | null = null;
  allowedFileTypes: MediaType[] = [];
  allowedFileExtensions: string[] = [
    '.mp4',
    '.m4v',
    '.m4a',
    '.wav',
    '.pptx',
    '.ppt',
    '.xlsx',
    '.xls',
    '.docx',
    '.doc',
    '.mp3',
    '.pdf',
    '.tif',
    '.tiff',
  ];
  ALLOWED_MIME_TYPES!: Record<string, number[]>;

  readonly AttachmentMode = AttachmentMode;

  loading$: Observable<boolean> = of(false);

  constructor(
    private readonly fb: FormBuilder,
    private readonly drawerService: DrawerService,
    private readonly loaderService: LoaderService,
    private readonly studentAttachmentService: StudentAttachmentService,
    private readonly folderSrv: AttachmentFolderService,
    private readonly message: NzMessageService,
    private readonly commonService: CommonService,
    private readonly cd: ChangeDetectorRef,
    private readonly auditContextService: AuditContextService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.initForm();
    this.loadMediaTypes();
    this.setupDrawerSubscription();
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    if (this.attachmentForm.invalid) {
      this.attachmentForm.markAllAsTouched();
      return;
    }
    if (this.attachmentForm.invalid) return;

    this.loaderService.show();
    const formValue = this.attachmentForm.value;

    // Build audit trail for modified fields - only include if there are changes
    const _modifiedFields = this.buildAttachmentModifiedFields(formValue);
    const baseRequest = {
      caseAttachmentId: undefined,
      caseId: this.caseParams.case.id,
      folderId: formValue.folderId,
      planTypeId: this.planTypeId,
      name: formValue.name,
      description: formValue.description,
      mediaTypeId: formValue.type,
      file: formValue.attachment,
      isQuestionnairePDF: formValue.isQuestionnairePDF || false,
      _modifiedFields: _modifiedFields, // Include modified fields in the request payload
    };

    const request$ =
      this.mode === AttachmentMode.CREATE
        ? this.studentAttachmentService.uploadAttachment({
            ...baseRequest,
          })
        : this.studentAttachmentService.updateAttachment({
            id: formValue.id,
            ...baseRequest,
          });

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          const message =
            this.mode === AttachmentMode.CREATE
              ? 'Attachment added successfully'
              : 'Attachment updated successfully';
          this.commonService.notify('success', message);
          this.closeDrawer();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private initForm(): void {
    this.attachmentForm = this.fb.group({
      id: [''],
      folderId: [1, [Validators.required]],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(this.MIN_NAME_LENGTH),
          Validators.maxLength(this.MAX_NAME_LENGTH),
        ],
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.minLength(this.MIN_DESCRIPTION_LENGTH),
          Validators.maxLength(500),
        ],
      ],
      type: ['', Validators.required],
      attachment: [
        null,
        this.mode === AttachmentMode.EDIT ? null : Validators.required,
      ],
    });

    if (this.mode === AttachmentMode.VIEW) {
      this.attachmentForm.disable();
    }
  }

  getFolderList() {
    this.folderSrv
      .getFolders(this.caseParams?.case?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.folders = response.result;
          this.cd.detectChanges();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  loadMediaTypes(searchTerm = '') {
    this.loaderService.show();
    this.studentAttachmentService
      .getMediaTypes(searchTerm)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: response => {
          this.allowedFileTypes = response.result;
          this.buildMimeTypesMapping();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loaderService.hide();
        },
      });
  }

  private buildMimeTypesMapping(): void {
    this.ALLOWED_MIME_TYPES = {
      'image/tiff': this.getFileTypeIds(['Tif', 'Tiff']),
      'video/mp4': this.getFileTypeIds(['.mp4']),
      'video/x-m4v': this.getFileTypeIds(['.m4v']),
      'audio/m4a': this.getFileTypeIds(['.m4a']),
      'audio/wav': this.getFileTypeIds(['.wav']),
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        this.getFileTypeIds(['.pptx']),
      'application/vnd.ms-powerpoint': this.getFileTypeIds(['.ppt']),
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        this.getFileTypeIds(['.xlsx']),
      'application/vnd.ms-excel': this.getFileTypeIds(['.xls']),
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        this.getFileTypeIds(['.docx']),
      'application/msword': this.getFileTypeIds(['.doc']),
      'audio/mpeg': this.getFileTypeIds(['.mp3']),
      'application/pdf': this.getFileTypeIds(['.pdf']),
    };
  }

  private getFileTypeIds(extensions: string[]): number[] {
    if (!this.allowedFileTypes) {
      return [];
    }

    return this.allowedFileTypes
      .filter(type => extensions.some(ext => type.value.includes(ext)))
      .map(type => type.id);
  }

  onSearch(value: string): void {
    this.loadMediaTypes(value);
  }

  findIdByExtension(extension: string) {
    const result = find(this.allowedFileTypes, item =>
      item.value.includes(`.${extension}`)
    );
    return result ? result.id : null;
  }

  beforeUpload = (file: NzUploadFile): boolean /* NOSONAR */ => {
    // allow upstream callers to handle the case where no file was provided
    if (!file) return false;

    if (file.size && file.size > this.MAX_FILE_SIZE) {
      this.attachmentForm.get('attachment')?.setErrors({ maxSize: true });
      this.message.error('File size cannot exceed 5MB');
      return false;
    }

    this.fileList = [file];
    const extension = file.name.split('.').pop() || '';
    this.attachmentForm.patchValue({
      attachment: file,
      type: this.findIdByExtension(extension),
    });

    return false;
  };

  handleRemove = (): boolean => {
    this.fileList = [];
    this.attachmentForm.patchValue({ attachment: null, type: null });
    this.attachmentForm.get('attachment')?.markAsTouched();
    // return true only when the file list is actually empty (varies with state)
    return this.fileList.length === 0;
  };

  onEdit(): void {
    this.mode = AttachmentMode.EDIT;
    this.drawerService.updateDrawerTitle(
      DRAWER_IDS.STUDENT_ATTACHMENT,
      'Edit Attachment'
    );
    this.attachmentForm.enable();
  }

  private buildAttachmentModifiedFields(
    formValue: Record<string, unknown>
  ): AuditFieldChange[] {
    const labelMap: Record<string, string> = {
      folderId: 'Folder',
      name: 'Attachment Name',
      description: 'Description',
      type: 'File Type',
      attachment: 'Attachment File',
    };

    const sensitiveFields: string[] = ['attachment'];
    const excludeFields = ['id', 'caseId'];

    // Create original object for comparison
    const original =
      this.mode === AttachmentMode.EDIT && this.caseParams?.attachment
        ? {
            folderId: this.caseParams.attachment.folderId || null,
            name: this.caseParams.attachment.name || '',
            description: this.caseParams.attachment.description || '',
            type: this.caseParams.attachment.mediaTypeId || null,
            attachment: this.caseParams.attachment.fileUrl || null,
          }
        : {};

    // Build field changes using AuditContextService
    let modifiedFields = this.auditContextService.buildFieldChanges(
      original,
      formValue,
      { sensitiveFields, labelMap, excludeFields }
    );

    modifiedFields = modifiedFields.filter(change => {
      if (change.field === 'attachment') {
        const oldUrl =
          typeof change.oldValue === 'string' ? change.oldValue : '';
        let newUrl = '';
        if (typeof change.newValue === 'string') {
          newUrl = change.newValue;
        } else if (
          typeof change.newValue === 'object' &&
          change.newValue !== null &&
          'url' in change.newValue
        ) {
          // Try to extract url property from object
          // @ts-expect-error: newValue may be a file info object
          newUrl = change.newValue.url || '';
        }
        // Only log if URLs differ
        return oldUrl !== newUrl;
      }
      return true;
    });

    return modifiedFields;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.STUDENT_ATTACHMENT);
  }

  cancleDrawer(): void {
    if (this.isEditMode) {
      this.mode = AttachmentMode.VIEW;
      this.drawerService.updateDrawerTitle(
        DRAWER_IDS.STUDENT_ATTACHMENT,
        'View Attachment'
      );

      if (this.caseParams?.attachment) {
        const fileInfo = {
          uid: this.caseParams.attachment.id.toString(),
          name: this.caseParams.attachment.name,
          status: 'done' as const,
          url: this.caseParams.attachment.fileUrl,
          size: Number(this.caseParams.attachment.fileSize),
        };

        this.attachmentForm.patchValue({
          id: this.caseParams.attachment.id,
          folderId: this.caseParams.attachment.folderId,
          name: this.caseParams.attachment.name,
          description: this.caseParams.attachment.description,
          type: this.caseParams.attachment.mediaTypeId,
          attachment: fileInfo,
        });
        this.fileList = [fileInfo];
      }
      this.attachmentForm.disable();
    } else {
      this.drawerService.closeDrawer(DRAWER_IDS.STUDENT_ATTACHMENT);
    }
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageDrawerParams>(DRAWER_IDS.STUDENT_ATTACHMENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (params: ManageDrawerParams | null) => {
        if (!params) return;
        this.caseParams = params;
        this.getFolderList();
        this.mode = params.mode as AttachmentMode;
        this.attachmentForm.reset();
        this.fileList = [];
        this.planTypeId = params.planTypeId ? params.planTypeId : null;
        if (params.attachment) {
          const fileInfo = {
            uid: params.attachment.id.toString(),
            name: params.attachment.name,
            status: 'done' as const,
            url: params.attachment.fileUrl,
            size: Number(params.attachment.fileSize),
          };

          const formValues = {
            id: params.attachment.id,
            folderId: params.attachment.folderId,
            name: params.attachment.name,
            description: params.attachment.description,
            type: params.attachment.mediaTypeId,
            attachment: fileInfo,
            fileInfo: {
              url: params.attachment.fileUrl,
              size: params.attachment.fileSize,
            },
          };
          switch (this.mode) {
            case AttachmentMode.VIEW:
              this.attachmentForm.patchValue(formValues);
              this.attachmentForm.disable();
              this.fileList = [fileInfo];
              break;

            case AttachmentMode.EDIT:
              this.attachmentForm.enable();
              this.attachmentForm.patchValue(formValues);
              this.attachmentForm.get('attachment')?.setValidators(null);
              this.fileList = [fileInfo];
              break;

            case AttachmentMode.CREATE:
              this.attachmentForm.enable();
              break;
          }
        }
      });
  }

  get isViewMode(): boolean {
    return this.mode === AttachmentMode.VIEW;
  }

  get isEditMode(): boolean {
    return this.mode === AttachmentMode.EDIT;
  }

  get isCreateMode(): boolean {
    return this.mode === AttachmentMode.CREATE;
  }
}
